import axios from '@shared/axios-shared';

/**
 * Hosted-page session: the short-lived (60 min) Sanctum token minted by the
 * verify step, or a token handed off by the mobile app webview. Kept in
 * sessionStorage (per-tab, gone on close) — never localStorage, since the
 * page runs on a shared/public device more often than the admin app.
 */
const KEY = 'pay.session';
const PENDING_KEY = 'pay.pendingSetup';

const applyHeader = (token) => {
	if (token) {
		axios.defaults.headers.common.Authorization = `Bearer ${token}`;
	} else {
		delete axios.defaults.headers.common.Authorization;
	}
};

const get = () => {
	try {
		const raw = sessionStorage.getItem(KEY);
		if (!raw) return null;
		const session = JSON.parse(raw);
		if (!session.access_token || new Date(session.expires_at).getTime() <= Date.now()) {
			clear();
			return null;
		}
		return session;
	} catch {
		return null;
	}
};

const save = (session) => {
	sessionStorage.setItem(KEY, JSON.stringify(session));
	applyHeader(session.access_token);
};

const clear = () => {
	sessionStorage.removeItem(KEY);
	applyHeader(null);
};

/** Re-attach the auth header on page load / 3DS return. */
const restore = () => {
	const session = get();
	if (session) applyHeader(session.access_token);
	return session;
};

/**
 * Card 28 webview hand-off: the app opens
 * `/auto-pay?student_id=X#session_token=<token>`. The token rides the URL
 * FRAGMENT so it never reaches server logs or Referer headers; we adopt it
 * into sessionStorage and immediately strip it from the address bar.
 */
const adoptFromUrl = () => {
	const match = window.location.hash.match(/session_token=([^&]+)/);
	if (!match) return null;

	const params = new URLSearchParams(window.location.search);
	const session = {
		access_token: decodeURIComponent(match[1]),
		// The app minted the token; assume the standard 60-min lifetime. A 401
		// from an earlier real expiry routes to the expired screen anyway.
		expires_at: new Date(Date.now() + 60 * 60 * 1000).toISOString(),
		student_id: params.get('student_id') ? Number(params.get('student_id')) : null,
		purpose: 'auto_pay',
		assignment_id: null,
		webview: true,
	};
	save(session);
	window.history.replaceState(null, '', window.location.pathname + window.location.search);
	return session;
};

/** Stash for resuming the enable sequence after a 3DS redirect. */
const savePendingSetup = (pending) => sessionStorage.setItem(PENDING_KEY, JSON.stringify(pending));
const getPendingSetup = () => {
	try {
		return JSON.parse(sessionStorage.getItem(PENDING_KEY)) || null;
	} catch {
		return null;
	}
};
const clearPendingSetup = () => sessionStorage.removeItem(PENDING_KEY);

export default {
	get,
	save,
	clear,
	restore,
	adoptFromUrl,
	savePendingSetup,
	getPendingSetup,
	clearPendingSetup,
};
